import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, map, of, take } from 'rxjs';
import { ChurchAffiliationInvite, isJoinRequest } from '../../../models/church-affiliation-invite.model';
import { HierarchyNode } from '../../../models/hierarchy-node.model';
import { ManagementAssignment } from '../../../models/management-assignment.model';
import { Organization } from '../../../models/organization.model';
import { ManagementService } from '../../../services/management.service';
import * as ManagementActions from '../../../state/actions/management.actions';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import * as ManagementSelectors from '../../../state/selectors/management.selectors';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { AlertType, DlgConfirmComponent } from '../../components/dialogs/dlg-confirm/dlg-confirm.component';

@Component({
  selector: 'app-church-affiliation',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, BtnBackComponent],
  templateUrl: './church-affiliation.component.html',
  styleUrl: './church-affiliation.component.css',
})
export class ChurchAffiliationComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private store = inject(Store<AppState>);
  private managementService = inject(ManagementService);
  private dialog = inject(MatDialog);
  private toastr = inject(ToastrService);
  private logger = inject(NGXLogger);
  private subs = new Subscription();

  churchId: string = '';

  // Church admins act on invitations and can leave the organization
  myRoles$: Observable<any> = of(null);
  invites$: Observable<ChurchAffiliationInvite[]> = of([]); // invitations from organizations
  joinRequests$: Observable<ChurchAffiliationInvite[]> = of([]); // requests this church sent
  respondingInviteId: string | null = null;
  rejectingInviteId: string | null = null;
  rejectReason: string = '';
  leaveReason: string = '';
  isLeaving: boolean = false;

  // Request to join an organization
  orgSearchTerm: string = '';
  orgSearchResults: Organization[] = [];
  isSearchingOrgs: boolean = false;
  joinMessage: string = '';
  requestingOrgId: string | null = null;
  private orgSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private orgSearchSeq = 0;

  // NgRx Selectors
  oversight$: Observable<{
    organization: Organization | null;
    lineage: HierarchyNode[];
    leaders: ManagementAssignment[];
  } | null> = this.store.select(ManagementSelectors.selectChurchOversight);

  loading$: Observable<boolean> = this.store.select(
    ManagementSelectors.selectManagementLoading
  );

  ngOnInit(): void {
    this.subs.add(
      this.route.params.subscribe((params) => {
        const id = params['id'];
        if (id) {
          this.churchId = id;
          this.store.dispatch(
            ManagementActions.loadChurchOversight({ churchId: id })
          );
          this.myRoles$ = this.store.select(groupSelector.getMyRolesInGroup(id));
          const pending$ = this.managementService.getChurchAffiliationInvites(id);
          this.invites$ = pending$.pipe(map((list) => list.filter((i) => !isJoinRequest(i))));
          this.joinRequests$ = pending$.pipe(map((list) => list.filter((i) => isJoinRequest(i))));
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.orgSearchTimer) clearTimeout(this.orgSearchTimer);
  }

  onOrgSearchChange(term: string): void {
    this.orgSearchTerm = term;
    if (this.orgSearchTimer) clearTimeout(this.orgSearchTimer);
    if (term.trim().length < 2) {
      this.orgSearchResults = [];
      this.isSearchingOrgs = false;
      return;
    }
    this.isSearchingOrgs = true;
    const seq = ++this.orgSearchSeq;
    this.orgSearchTimer = setTimeout(async () => {
      try {
        const results = await this.managementService.searchOrganizations(term);
        if (seq === this.orgSearchSeq) this.orgSearchResults = results;
      } catch (err) {
        this.logger.error('church-affiliation.component.ts :: Organization search failed:', { term, err });
        if (seq === this.orgSearchSeq) this.orgSearchResults = [];
      } finally {
        if (seq === this.orgSearchSeq) this.isSearchingOrgs = false;
      }
    }, 300);
  }

  hasPendingRequestTo(orgId: string, requests: ChurchAffiliationInvite[], invites: ChurchAffiliationInvite[]): boolean {
    return [...requests, ...invites].some((r) => r.organizationId === orgId);
  }

  async requestToJoin(organization: Organization): Promise<void> {
    if (this.requestingOrgId) return;
    this.logger.debug('church-affiliation.component.ts :: Request to join clicked:', { orgId: organization.id, churchId: this.churchId });
    this.requestingOrgId = organization.id;
    try {
      await this.managementService.requestToJoinOrganization(organization.id, this.churchId, this.joinMessage);
      this.toastr.success(`Request sent. ${organization.name}'s admins will review it.`);
      this.joinMessage = '';
      this.orgSearchTerm = '';
      this.orgSearchResults = [];
    } catch (err: any) {
      this.logger.error('church-affiliation.component.ts :: Request to join failed:', { orgId: organization.id, churchId: this.churchId, err });
      this.toastr.error(err?.message || 'Could not send the request.');
    } finally {
      this.requestingOrgId = null;
    }
  }

  async withdrawRequest(request: ChurchAffiliationInvite): Promise<void> {
    this.logger.debug('church-affiliation.component.ts :: Withdraw request clicked:', { inviteId: request.id });
    try {
      await this.managementService.cancelChurchInvite(request.id);
      this.toastr.info(`Request to ${request.organizationName} withdrawn.`);
    } catch (err: any) {
      this.logger.error('church-affiliation.component.ts :: Withdrawing join request failed:', { inviteId: request.id, err });
      this.toastr.error(err?.message || 'Could not withdraw the request.');
    }
  }

  confirmAcceptInvite(invite: ChurchAffiliationInvite): void {
    this.logger.debug('church-affiliation.component.ts :: Accept & Join clicked:', { inviteId: invite.id, orgId: invite.organizationId });
    this.openConfirm(
      `Join ${invite.organizationName}`,
      `This church will be placed under ${invite.targetNodeName} in ${invite.organizationName}'s org chart. ` +
        `All admins of this church will become members of the organization with church-level access. ` +
        `The organization will see attendance and growth figures, but never finances. ` +
        `Any other pending invitations will be closed.`,
      'Accept & Join',
      'bg-primary-500',
      AlertType.info
    ).subscribe((confirmed) => {
      this.logger.debug('church-affiliation.component.ts :: Accept dialog closed:', { inviteId: invite.id, confirmed });
      if (confirmed) this.respond(invite, true);
    });
  }

  startReject(invite: ChurchAffiliationInvite): void {
    this.logger.debug('church-affiliation.component.ts :: Decline clicked:', { inviteId: invite.id });
    this.rejectingInviteId = invite.id;
    this.rejectReason = '';
  }

  cancelReject(): void {
    this.rejectingInviteId = null;
    this.rejectReason = '';
  }

  async respond(invite: ChurchAffiliationInvite, accept: boolean): Promise<void> {
    if (this.respondingInviteId) return;
    this.logger.debug('church-affiliation.component.ts :: Responding to invitation:', { inviteId: invite.id, accept });
    this.respondingInviteId = invite.id;
    try {
      await this.managementService.respondToChurchInvite(invite.id, accept, accept ? undefined : this.rejectReason);
      if (accept) {
        this.toastr.success(`Welcome to ${invite.organizationName}! This church is now part of its org chart.`);
      } else {
        this.toastr.info(`Invitation from ${invite.organizationName} declined.`);
      }
      this.cancelReject();
    } catch (err: any) {
      this.logger.error('church-affiliation.component.ts :: Responding to invitation failed:', { inviteId: invite.id, accept, err });
      this.toastr.error(err?.message || 'Could not respond to the invitation.');
    } finally {
      this.respondingInviteId = null;
    }
  }

  confirmLeave(organization: Organization): void {
    this.logger.debug('church-affiliation.component.ts :: Leave organization clicked:', { orgId: organization.id, churchId: this.churchId });
    this.openConfirm(
      `Leave ${organization.name}`,
      `This church will be removed from ${organization.name}'s org chart. Church-level roles in the organization ` +
        `will be revoked, and admins who were only members because of this church will leave the organization. ` +
        `The organization's admins will be notified.`,
      'Leave',
      'bg-red-500',
      AlertType.warning
    ).subscribe(async (confirmed) => {
      this.logger.debug('church-affiliation.component.ts :: Leave dialog closed:', { orgId: organization.id, confirmed });
      if (!confirmed) return;
      this.isLeaving = true;
      try {
        await this.managementService.detachChurch(organization.id, this.churchId, this.leaveReason);
        this.toastr.success(`This church has left ${organization.name}.`);
        this.leaveReason = '';
      } catch (err: any) {
        this.logger.error('church-affiliation.component.ts :: Leaving organization failed:', { orgId: organization.id, churchId: this.churchId, err });
        this.toastr.error(err?.message || 'Could not leave the organization.');
      } finally {
        this.isLeaving = false;
      }
    });
  }

  private openConfirm(
    title: string,
    description: string,
    action: string,
    buttonColor: string,
    showType: AlertType
  ): Observable<boolean> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = ['!p-0', '!bg-transparent', '!shadow-none'];
    dialogConfig.data = { title, description, action, buttonColor, showType, showCancel: true };
    return this.dialog.open(DlgConfirmComponent, dialogConfig).afterClosed().pipe(take(1));
  }
}
